import { useEffect, useMemo, useState } from 'react';

export function usePopupManager({
    accessToken,
    isLogin,
    email,
    level,
    initialPopupStatus = {},
}) {
    const [updates, setUpdates] = useState([]);
    const [activeGroup, setActiveGroup] = useState(null);
    const [activeUpdate, setActiveUpdate] = useState(null);
    const [popupStatusByGroup, setPopupStatusByGroup] = useState(initialPopupStatus || {});
    const [modalUpdate, setModalUpdate] = useState([]);
    const [isUpdatesModalOpen, setIsUpdatesModalOpen] = useState(false);
    const [dismissedGroups, setDismissedGroups] = useState([]);

    useEffect(() => {
        if (!accessToken) return;

        fetch(`${process.env.REACT_APP_API_URL}/get_platform_updates?email=${encodeURIComponent(email)}`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        })
            .then((res) => res.json())
            .then((data) => {
                const arr = Array.isArray(data) ? data : [data];

                const normalized = arr.map((u) => ({
                    ...u,
                    group: u.group || 'default',
                    priority: u.priority ?? 999,
                }));


                normalized.sort((a, b) => a.priority - b.priority);


                setUpdates(normalized);
            })
            .catch((err) => {
                console.error('Erro ao buscar updates:', err);
            });
    }, [accessToken, email]);

    const groupedUpdates = useMemo(() => {
        return updates.reduce((acc, update) => {
            const group = update.group;

            if (!acc[group]) acc[group] = [];
            acc[group].push(update);

            return acc;
        }, {});
    }, [updates]);

    useEffect(() => {
        if (!isLogin || level === 'externo' || updates.length === 0) return;

        // pega os grupos fixos primeiro, depois adiciona qualquer outro que vier da API
        const fixedPriority = ['onboarding', 'updates', 'feature', 'default'];
        const allGroups = Object.keys(groupedUpdates);
        const groupPriority = [
            ...fixedPriority.filter(g => allGroups.includes(g)),
            ...allGroups.filter(g => !fixedPriority.includes(g))
        ];

        let found = false;

        for (const group of groupPriority) {
            const groupUpdates = groupedUpdates[group];
            if (!groupUpdates?.length) continue;

            const status = popupStatusByGroup[group];

            const neverSee = status === 'no_see';
            if (neverSee) continue;

            if (dismissedGroups.includes(group)) continue;

            const update = groupUpdates[0];

            setActiveGroup(group);
            setActiveUpdate(update);

            found = true;
            break;
        }

        if (!found) {
            setActiveUpdate(null);
        }

    }, [groupedUpdates, popupStatusByGroup, dismissedGroups, isLogin, level]);


    const handleTestNow = () => {
        setModalUpdate((prev) => [...prev, activeUpdate]);
        setDismissedGroups((prev) => [...prev, activeGroup]);

        //updateGroupStatus('try_now');
        setActiveUpdate(null);
        setIsUpdatesModalOpen(true);

        try {
            localStorage.setItem('updates_flow', 'modal');
        } catch { }
    };

    const handleSeeLater = () => {
        //updateGroupStatus('see_later');
        setDismissedGroups((prev) => [...prev, activeGroup]);
        setActiveUpdate(null);
    };

    const handleDontShowAgain = () => {
       if (!activeGroup) return;

       setPopupStatusByGroup((prev) => ({ ...prev, [activeGroup]: 'no_see' }));
       
       setModalUpdate([]);
       setIsUpdatesModalOpen(false);
    };

    const handleCloseModal = () => {
        setIsUpdatesModalOpen(false);

        try {
            sessionStorage.setItem('updates_modal_shown', 'true');
        } catch { }
    };

    const combinedModalSteps = (modalUpdate || []).flatMap((u) => u?.steps || []);

    return {
        // dados ativos
        activeUpdate,
        activeGroup,

        // estados
        isFiveUpdatesOpen: !!activeUpdate,
        isUpdatesModalOpen,

        // dados para UI
        stepsData: activeUpdate?.steps || [],
        config: activeUpdate?.config || {},

        modalStepsData: combinedModalSteps,
        modalConfig: modalUpdate[0]?.config || {},
        modalUpdateId: modalUpdate.map((u) => u.update_id).join(','),

        groupedUpdates, popupStatusByGroup,

        // ações
        handleTestNow,
        handleSeeLater,
        handleDontShowAgain,
        handleCloseModal,
        setIsUpdatesModalOpen,
        setPopupStatusByGroup,
        setModalUpdate,
        setIsUpdatesModalOpen
    };
}